<script setup lang="ts">
import { onMounted, ref } from 'vue'
import { Palette } from 'lucide-vue-next'
import { useI18n } from 'vue-i18n'

import Popover from 'primevue/popover'

import AppButton from '@/components/atoms/buttons/AppButton.vue'
import { useThemeSwitcher, type ThemeColor } from '@/composables/useThemeSwitcher'

const { currentColor, availableColors, changeThemeColor, initTheme } = useThemeSwitcher()
const { t } = useI18n()
const op = ref()

const togglePanel = (event: Event) => {
  op.value.toggle(event)
}

const selectColor = (color: ThemeColor) => {
  changeThemeColor(color)
  op.value.hide()
}

onMounted(() => {
  initTheme()
})
</script>

<template>
  <div>
    <AppButton
      severity="secondary"
      size="small"
      outlined
      rounded
      :block="false"
      v-tooltip.bottom="t('header.actions.change_theme')"
      @click="togglePanel"
      class="app-button-header-glass shadow-md min-w-10 hover:bg-white/50! dark:hover:bg-[var(--p-primary-600)]/50!  !w-10 !h-10 !p-2 flex-shrink-0 flex items-center justify-center transition-all duration-200 hover:scale-105 active:scale-95"
    >
      <template #icon-start>
        <Palette class="w-5 h-5" :stroke-width="1.5" />
      </template>
    </AppButton>

    <Popover ref="op" class="!p-3">
      <div class="flex flex-col gap-2">
        <h3 class="text-xs font-semibold text-gray-700 dark:text-gray-200 m-0 mb-1">
          {{ t('header.actions.theme_color') }}
        </h3>
        <div class="grid grid-cols-9 gap-1.5">
          <button
            v-for="color in availableColors"
            :key="color.value"
            @click="selectColor(color.value)"
            :aria-label="`Color ${color.label}`"
            class="w-6 h-6 rounded-md transition-all duration-200 hover:scale-110 focus:outline-none focus:ring-2 focus:ring-offset-1 focus:ring-offset-white dark:focus:ring-offset-neutral-900 relative"
            :class="{
              'ring-2 ring-offset-1 ring-offset-white dark:ring-offset-neutral-900 scale-110':
                currentColor === color.value,
            }"
            :style="{
              backgroundColor: color.hex,
              'box-shadow':
                currentColor === color.value
                  ? `0 0 0 2px ${color.hex}40`
                  : '0 1px 2px 0 rgb(0 0 0 / 0.1)',
            }"
          >
            <span
              v-if="currentColor === color.value"
              class="absolute inset-0 flex items-center justify-center"
            >
              <svg
                class="w-3.5 h-3.5 text-white drop-shadow-md"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="3"
                  d="M5 13l4 4L19 7"
                ></path>
              </svg>
            </span>
          </button>
        </div>
      </div>
    </Popover>
  </div>
</template>

<style scoped>
button:focus-visible {
  outline: 2px solid currentColor;
  outline-offset: 2px;
}
</style>
